import type { operations } from "../types/generated/api";
import type { Schema } from "../types/generated";
import { httpClient } from "./client";

type BusinessQuery =
  operations["search_reservation_operations_facilities__facility_id__reservation_operations_get"]["parameters"]["query"];
type PlatformQuery =
  operations["search_reservation_operations_platform_reservation_operations_get"]["parameters"]["query"];

export type ReservationOperation = Schema["ReservationOperationItem"];
export type ReservationOperationsPage = Schema["ReservationOperationsPage"];
export type ReservationOperationStatus = NonNullable<
  BusinessQuery["status"]
>[number];
export type ReservationOperationSort = NonNullable<BusinessQuery["sort_by"]>;
export type ReservationOperationSortDirection = NonNullable<
  PlatformQuery["sort_direction"]
>;

export const MAX_RESERVATION_OPERATIONS_OFFSET = 10_000;

export interface ReservationOperationsParams {
  fromDate: BusinessQuery["from_date"];
  toDate: BusinessQuery["to_date"];
  facilityIds?: NonNullable<BusinessQuery["facility_id"]>;
  statuses?: NonNullable<BusinessQuery["status"]>;
  query?: BusinessQuery["q"];
  sortBy?: BusinessQuery["sort_by"];
  sortDirection?: ReservationOperationSortDirection;
  limit?: BusinessQuery["limit"];
  offset?: BusinessQuery["offset"];
}

export interface PlatformReservationOperationsParams
  extends ReservationOperationsParams {
  organizationIds?: NonNullable<PlatformQuery["organization_id"]>;
}

function buildQuery(
  params: PlatformReservationOperationsParams,
): URLSearchParams {
  const search = new URLSearchParams();
  search.set("from_date", params.fromDate);
  search.set("to_date", params.toDate);
  params.organizationIds?.forEach((id) => search.append("organization_id", id));
  params.facilityIds?.forEach((id) => search.append("facility_id", id));
  params.statuses?.forEach((status) => search.append("status", status));
  const query = params.query?.trim();
  if (query) search.set("q", query);
  if (params.sortBy) search.set("sort_by", params.sortBy);
  if (params.sortDirection) {
    search.set("sort_direction", params.sortDirection);
  }
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  return search;
}

export function searchBusinessReservationOperations(
  anchorFacilityId: string,
  params: ReservationOperationsParams,
): Promise<ReservationOperationsPage> {
  const query = buildQuery(params);
  return httpClient.get<ReservationOperationsPage>(
    `/facilities/${encodeURIComponent(anchorFacilityId)}/reservation-operations?${query.toString()}`,
  );
}

export function searchPlatformReservationOperations(
  params: PlatformReservationOperationsParams,
): Promise<ReservationOperationsPage> {
  const query = buildQuery(params);
  return httpClient.get<ReservationOperationsPage>(
    `/platform/reservation-operations?${query.toString()}`,
  );
}
